import * as Sentry from "@sentry/react";
import {
  RouterProvider,
  createHashHistory,
  createRouter,
} from "@tanstack/react-router";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";

import { createDB } from "./db";
import { elementsQueryOptions } from "./hooks/queries/elementsQuery";
import { profilesQueryOptions } from "./hooks/queries/profilesQuery";
import { noteVaultsQueryOptions } from "./hooks/queries/useGetNoteVaults";
import { todoVaultsQueryOptions } from "./hooks/queries/useGetTodoVaults";
import { userQueryOptions } from "./hooks/queries/userQuery";
import { AnalyticsProvider, initPosthogClient } from "./lib/analytics";
import { queryClient } from "./lib/query";
import { setRequestAccessToken } from "./lib/request";
import { routeTree } from "./routeTree.gen";
import { store } from "./store";

// Fonts are imported here (not via @import in index.css) so Vite resolves the
// woff2 url()s against the packages and emits the files; Tailwind's CSS
// inliner kept the relative ./files/ URLs, which broke fonts in production.
import "@fontsource/lato";
import "@fontsource-variable/montserrat";
import "./index.css";

if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // RUM: page-load/navigation traces + web vitals. Default same-origin trace
    // propagation only — the API's CORS does not allow sentry-trace/baggage yet.
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
  });
}

// Exported so the `Register` module augmentation in vite-env.d.ts resolves
// (`typeof router`) and typed Link/navigate/useSearch work across the app.
export const router = createRouter({
  routeTree,
  history: import.meta.env.MANIFEST_VERSION ? createHashHistory() : undefined,
  // Preload on hover/focus and keep pending UI flicker-free.
  defaultPreload: "intent",
  defaultPreloadStaleTime: 30_000,
  defaultPendingMs: 200,
  defaultPendingMinMs: 500,
});

// Warm the query cache with the boot-critical requests before React mounts.
// The provider hooks use these exact keys/fns, so they dedupe against the
// in-flight prefetches instead of re-serializing the waterfall
// (token → user/profile → elements → widget queries). Fire-and-forget.
function prefetchBootQueries() {
  const accessToken = localStorage.getItem("unideck.auth.accessToken");
  if (!accessToken) {
    return;
  }

  setRequestAccessToken(accessToken);

  void queryClient.prefetchQuery(userQueryOptions(accessToken));
  void queryClient.prefetchQuery(profilesQueryOptions());
  void queryClient.prefetchQuery(noteVaultsQueryOptions());
  void queryClient.prefetchQuery(todoVaultsQueryOptions());

  const storedProfileId = localStorage.getItem("unideck.activeProfileId");
  if (storedProfileId) {
    void queryClient.prefetchQuery(elementsQueryOptions(storedProfileId));
  }
}

function start() {
  prefetchBootQueries();
  // IndexedDB opens in the background; consumers await getDB() readiness
  // instead of gating the first render on it.
  void createDB();
  createRoot(document.getElementById("root")!).render(
    <Provider store={store}>
      <AnalyticsProvider>
        <RouterProvider router={router} />
      </AnalyticsProvider>
    </Provider>,
  );

  // Deferred: loads posthog-js off the critical path (idle after first paint).
  initPosthogClient();
}

start();
